import { useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import SalesVoidPreviewPanel from "./SalesVoidPreviewPanel";
import SalesReturnPreviewPanel from "./SalesReturnPreviewPanel";

export default function SalesPostingPanel({
  companyId,
  saleId,
  candidate,
  accessToken,
  canPost,
  canVoid = false,
  canReturn = false,
  onPosted,
}) {
  const [note, setNote] = useState(""),
    [preview, setPreview] = useState(null),
    [accepted, setAccepted] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [posted, setPosted] = useState(null);
  const key = useRef(null);
  const url = `/accounting/${companyId}/sales-posting/pos/${saleId}`;
  const existing = posted || candidate.accountingSource;
  if (existing)
    return (
      <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <h2 className="font-bold">Sale accounted</h2>
        <p className="mt-2 text-sm">
          Voucher {existing.voucher.number} · Original reviewed evidence
          retained. Ordinary POS voids and returns are blocked; supported cash
          corrections use the reviewed workflow below.
        </p>
        {existing.void ? (
          <p className="mt-3 text-sm font-semibold">
            Fully voided · Reversal voucher {existing.void.voucher.number}.
            Original records retained.
          </p>
        ) : (
          <>
            <SalesVoidPreviewPanel
              key={companyId + ":" + saleId}
              companyId={companyId}
              saleId={saleId}
              accessToken={accessToken}
              canExecute={canVoid}
              onVoided={onPosted}
            />
            <SalesReturnPreviewPanel
              key={"returns:" + companyId + ":" + saleId}
              companyId={companyId}
              saleId={saleId}
              accessToken={accessToken}
              canExecute={canReturn}
              onReturned={onPosted}
            />
          </>
        )}
      </section>
    );
  const eligible =
    candidate.latestSnapshotCurrent && !candidate.blockers.length;
  async function run(post) {
    setBusy(true);
    setError("");
    try {
      const body = { snapshotId: candidate.latestSnapshotId, reviewNote: note };
      if (post) {
        if (!key.current) {
          if (!window.crypto?.randomUUID)
            throw Error("A secure HTTPS or localhost connection is required.");
          key.current = window.crypto.randomUUID();
        }
        Object.assign(body, {
          requestKey: key.current,
          previewHash: preview.previewHash,
          priorEntriesReviewed: accepted,
          correctionRestrictionAccepted: accepted,
        });
      }
      const r = await apiRequest(url + (post ? "" : "/preview"), {
        accessToken,
        method: "POST",
        body,
      });
      if (post) {
        setPosted(r);
        onPosted();
      } else {
        setPreview(r);
        setAccepted(false);
        key.current = null;
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="space-y-4 rounded-2xl border border-amber-200 bg-white p-5">
      <h2 className="text-lg font-bold">Post reviewed sale to accounts</h2>
      <p className="text-sm text-slate-600">
        Creates one balanced source-linked voucher on the original India sale
        date. No new stock issue, tender collection or bank settlement. Only
        reviewed cash/card/UPI sales with eligible Outright lot costs are
        supported.
      </p>
      <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-950">
        Important: after posting, ordinary voids, returns and refunds on this
        receipt are blocked. Only the reviewed same-day cash full-void scope is
        supported for corrections; other refunds and returns remain unavailable.
      </p>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {!eligible && (
        <p className="text-sm text-slate-600">
          Save a current, unblocked tax and cost review before previewing
          financial posting.
        </p>
      )}
      {canPost && eligible && (
        <>
          <label className="block text-sm font-medium">
            Prior-entry and posting review note
            <textarea
              value={note}
              disabled={busy}
              onChange={(e) => {
                setNote(e.target.value);
                setPreview(null);
                setAccepted(false);
                key.current = null;
              }}
              minLength={20}
              maxLength={1000}
              className="mt-2 w-full rounded-xl border p-3"
              placeholder="Confirm cutover, prior manual entries and source evidence to avoid double recognition."
            />
          </label>
          <button
            type="button"
            disabled={busy || note.trim().length < 20}
            onClick={() => run(false)}
            className="rounded-xl border px-4 py-3 text-sm font-semibold disabled:opacity-40"
          >
            Preview accounting voucher
          </button>
          {preview && (
            <>
              <p className="text-sm">
                Posting date: {preview.payload.date}. Year and GST registration
                are taken from reviewed ownership.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Ledger</th>
                      <th>Debit ₹</th>
                      <th>Credit ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.payload.lines.map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-2">
                          {preview.ledgers?.find((x) => x.id === l.ledgerId)
                            ?.name || l.ledgerId}
                        </td>
                        <td>{l.debit}</td>
                        <td>{l.credit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={accepted}
                  disabled={busy}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                I checked that this sale has not already been recognised
                manually, verified the voucher, and accept that ordinary POS
                voids and returns will be blocked.
              </label>
              <button
                type="button"
                disabled={busy || !accepted}
                onClick={() => run(true)}
                className="rounded-xl bg-primary-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-40"
              >
                {busy ? "Working…" : "Post sale to accounts"}
              </button>
            </>
          )}
        </>
      )}
      {!canPost && (
        <p className="text-sm text-slate-500">
          Posting requires accounting.vouchers_post permission.
        </p>
      )}
    </section>
  );
}
